export const portal = {
  layout: {
    brand: 'WHATZNEU',
    product: 'Finance',
    customer: 'Customer',
    signOut: 'Sign out',
    nav: {
      home: 'Home',
      debits: 'Debits',
      payments: 'Payments',
      deposits: 'Deposits',
      products: 'Products',
      notifications: 'Notifications',
      profile: 'Profile',
      help: 'Help',
    },
    localTesting: 'Local testing. This portal is using the lead-service at {{host}}. Nothing is sent to production.',
  },
  login: {
    tagline: 'A clear view of your outstanding balance, payments, and Whatzneu products.',
    title: 'Sign in',
    intro: 'Existing customers sign in with a one-time code sent to their email. Whatzneu creates customer accounts.',
    guideLink: 'New here? Read the user guide',
    email: 'Email',
    sendCode: 'Send code',
    code: '6-digit code',
    verify: 'Verify and sign in',
    checking: 'Checking…',
    resend: 'Send a new code',
    differentEmail: 'Use a different email',
    expired: 'Your session has expired. Please sign in again.',
    codeSent: 'We sent a 6-digit code to {{email}}.',
    invalidEmail: 'Enter the email address on your Whatzneu account.',
    invalidCode: 'Enter the 6-digit code from your email.',
    sendFailed: "We couldn't send a code. Please try again.",
    verifyFailed: 'Please check the code and try again.',
  },
  common: {
    interestDateEachMonth: 'Interest date: {{day}} of each month',
    interestDateMissing: 'Interest date not on file',
    dayOfMonth: '{{day}} of each month',
    ratePerMonth: '{{value}}% a month',
    dateMissing: 'Date not on file',
    debit: 'Debit',
    payment: 'Payment',
    product: 'Product',
    notice: 'Notice',
    noteFromWhatzneu: 'Note from Whatzneu: {{note}}',
    columns: {
      date: 'Date',
      type: 'Type',
      amount: 'Amount',
      status: 'Status',
      note: 'Note',
      receivedBy: 'Received by',
      depositDate: 'Deposit date',
      maturity: 'Maturity',
      reference: 'Reference',
    },
    rowsPerPage: 'Rows per page',
    rowsRange: '{{from}}–{{to}} of {{count}}',
  },
  home: {
    welcome: 'Welcome back',
    hello: 'Hello, {{name}}',
    helloFallback: 'Hello',
    totalOutstanding: 'Total outstanding',
    requestFunds: 'Request funds',
    refer: 'Refer a customer',
    activeDebits: 'Your active debits',
    loadingDebits: 'Loading debits',
    noActiveDebits: 'You have no active debits.',
    recentPayments: 'Recent payments',
    loadingPayments: 'Loading payments',
    noPayments: 'No payments are recorded yet.',
  },
  debits: {
    kicker: 'Debits',
    title: 'All your debits',
    subtitle: 'Every facility on file, including closed ones. Select one to see its details.',
    loading: 'Loading debits',
    empty: 'No debits are on file.',
    outstanding: 'Outstanding',
    fields: {
      outstanding: 'Outstanding',
      originalAmount: 'Original amount',
      interestRate: 'Interest rate on file',
      interestDate: 'Interest date',
      lastInterest: 'Last interest collected',
      duration: 'Duration',
      startDate: 'Start date',
      maturityDate: 'Maturity date',
      instalment: 'Instalment on file',
      closed: 'Closed',
    },
    viewPayments: 'Payments and monthly trend',
  },
  debitPayments: {
    back: 'All debits',
    kicker: 'Payments',
    fallbackTitle: 'Debit payments',
    subtitle: 'Monthly totals use the payment amounts and dates recorded by Whatzneu. Nothing here is a calculated due amount.',
    loading: 'Loading payments',
    chartTitle: 'Recorded payments by month',
    noChart: 'No recorded payments to chart yet.',
    tooltip: 'Recorded',
    empty: 'No payments are recorded for this debit.',
  },
  payments: {
    kicker: 'Payments',
    title: 'Payment history',
    subtitle: 'Payments Whatzneu has recorded against your account.',
    loading: 'Loading payments',
    empty: 'No payments are recorded yet.',
  },
  deposits: {
    kicker: 'Deposits',
    title: 'Your deposits',
    subtitle: 'Deposits recorded with Whatzneu and their maturity dates.',
    activeBalance: 'Active deposit balance',
    loading: 'Loading deposits',
    empty: 'No deposits are on file.',
  },
  products: {
    kicker: 'Products',
    title: 'Explore Whatzneu',
    subtitle: 'Published products and services. Register interest and Whatzneu will contact you.',
    all: 'All products',
    loading: 'Loading products',
    empty: 'No products are available right now.',
    open: 'Open product',
    registerInterest: 'Register interest',
    requestsTitle: 'Your interest requests',
    requestsEmpty: 'You have not registered interest yet.',
    noMessage: 'No message',
    dialogTitle: 'Register interest in {{name}}',
    message: 'Message (optional)',
    thanks: 'Thanks. Whatzneu will contact you about {{name}}.',
    sendError: "We couldn't send your interest. Please try again.",
  },
  notifications: {
    kicker: 'Notifications',
    title: 'Notifications',
    subtitle: 'Facility updates and payment reminders from Whatzneu.',
    refresh: 'Refresh',
    markAll: 'Mark all as read',
    loading: 'Loading notifications',
    empty: "You're all caught up.",
    unreadHint: 'Unread · select to mark as read',
  },
  profile: {
    kicker: 'Profile',
    title: 'Your profile',
    subtitle: 'Details Whatzneu holds for your account. Contact your relationship manager to change them.',
    loading: 'Loading profile',
    account: 'Account',
    name: 'Name',
    email: 'Email',
    mobile: 'Mobile',
    address: 'Address',
    requests: 'Requests',
    sessionTitle: 'This session',
    sessionBody: 'You signed in with a one-time code sent to your email.',
    sessionUntil: 'This browser stays signed in until {{date}}, or until you sign out.',
  },
  referrals: {
    kicker: 'Referrals',
    title: 'Refer a customer',
    subtitle: 'Introduce someone who needs funds. The Whatzneu team reviews every referral and onboards them.',
    fullName: 'Full name',
    email: 'Email',
    mobile: 'Mobile number',
    amount: 'Amount required',
    submit: 'Send referral',
    listTitle: 'Your referrals',
    empty: 'No referrals yet.',
    thanks: 'Thanks. Whatzneu will contact {{name}} about onboarding.',
    sendError: "We couldn't send your referral. Please try again.",
  },
  requestFunds: {
    kicker: 'Request funds',
    title: 'Request funds',
    subtitle: 'Tell Whatzneu what you need. Every request is reviewed by the team before anything is offered.',
    exact: 'Exact amount you need',
    minimum: 'Minimum amount you can accept',
    rate: 'Monthly interest rate you can manage',
    rateHelper: 'Between 0.1% and 15% a month. Whatzneu confirms the final rate.',
    rateSuffix: '% a month',
    purpose: 'Purpose',
    submit: 'Send request',
    listTitle: 'Your requests',
    empty: 'No fund requests yet.',
    summaryLine: 'Minimum {{amount}} · {{rate}}% a month · {{date}}',
    sent: 'Your request was sent. Whatzneu will review it and contact you.',
    sendError: "We couldn't send your request. Please try again.",
  },
  validation: {
    referralName: 'Enter the new customer’s name.',
    email: 'Enter a valid email address.',
    mobile: 'Enter a 10-digit mobile number.',
    referralAmount: 'Enter the amount they need, in rupees.',
    fundExact: 'Enter the exact amount you need.',
    fundMinimum: 'Enter the minimum amount you can accept.',
    fundMinimumHigher: 'The minimum cannot be higher than the exact amount.',
    fundRate: 'Enter a monthly interest rate between 0.1% and 15%.',
    fundPurpose: 'Describe the purpose in a short sentence.',
  },
};
